/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect, useMemo, useState } from 'react';

import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { PageActivityFeatures } from 'constants/pageActivityFeatures';
import { useTranslate } from 'hooks/useTranslate';

import AnalyticsEmptyState from '../components/AnalyticsEmptyState';
import AnalyticsFilters from '../components/AnalyticsFilters';
import analyticsTableColumns from '../constants/analyticsTableColumns';
import { useAnalyticsEvents } from '../hooks/useAnalyticsEvents';
import { formatEventRow } from '../utils/formatEventRow';

type CustomLandingTabProps = {
  societyId?: string;
};

const formatLocalDate = (date: Date): string => date.toISOString().slice(0, 10);

const buildDefaultRange = (): { from: string; to: string } => {
  const today = new Date();
  const thirtyAgo = new Date(today);
  thirtyAgo.setDate(today.getDate() - 30);
  return { from: formatLocalDate(thirtyAgo), to: formatLocalDate(today) };
};

const CustomLandingTab: FC<CustomLandingTabProps> = ({ societyId }) => {
  const { i18n, t } = useTranslate();
  const defaults = useMemo(buildDefaultRange, []);
  const [fromDate, setFromDate] = useState<string>(defaults.from);
  const [toDate, setToDate] = useState<string>(defaults.to);
  const [columns, setColumns] = useState(() => analyticsTableColumns(i18n));

  const { events, isLoading } = useAnalyticsEvents({
    societyId,
    feature: PageActivityFeatures.CUSTOM_LANDING,
    fromDate,
    toDate,
  });

  const rows = useMemo(() => events.map(formatEventRow), [events]);

  useEffect(() => {
    setColumns(analyticsTableColumns(i18n));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="nk-block" data-testid="analytics-custom-landing-tab">
      <AnalyticsFilters
        fromDate={fromDate}
        toDate={toDate}
        onChangeFromDate={setFromDate}
        onChangeToDate={setToDate}
      />

      {rows.length > 0 ? (
        <CustomDataTable
          {...({
            data: rows,
            columns,
            pagination: true,
            searchBy: [
              'partnerLabel',
              'partnerEmail',
              'sectionTitle',
              'folderTitle',
              'contentTitle',
            ],
            searchByPlaceholder: t('FilterPartnerSearchPlaceholder'),
            rowsPerPage: 10,
            fileName: 'analytics-informes-resultados',
            defaultSortFieldId: 6,
            defaultSortAsc: false,
            showDownload: false,
          } as any)}
        />
      ) : (
        !isLoading && <AnalyticsEmptyState />
      )}

      {isLoading && <CustomLoading />}
    </div>
  );
};

export default CustomLandingTab;
